import { useState } from "react";
import { Form, Formik } from "formik";
import { Save } from "lucide-react";
import { toast } from "sonner";
import * as Yup from "yup";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { FormField } from "@/components/common/form-field";
import { archiveFta } from "@/features/transcripts/api";
import type { ForWho } from "@/features/transcripts/types";
import { ApiError } from "@/lib/api";

interface Props {
  clientId: string;
  forWho: ForWho;
  disabled?: boolean;
}

interface Values {
  docName: string;
  overwrite: boolean;
}

const schema = Yup.object({
  docName: Yup.string().trim().required("A document name is required."),
  overwrite: Yup.boolean(),
});

/** "Save to Documents" — renders the FTA PDF into the client's Letters folder.
 * If a same-named document exists and "replace existing" is off, the server
 * returns an exists-conflict and we prompt the user to confirm the overwrite. */
export function ArchiveFtaDialog({ clientId, forWho, disabled }: Props) {
  const [open, setOpen] = useState(false);

  async function handleSubmit(values: Values) {
    try {
      const result = await archiveFta(clientId, {
        docName: values.docName,
        overwrite: values.overwrite,
        forWho,
      });
      if ("exists" in result) {
        toast.warning(
          `A document named "${values.docName}" already exists — enable "Replace existing" to overwrite it.`,
        );
        return;
      }
      toast.success("Saved the FTA analysis to Documents.");
      setOpen(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not save the document.");
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Save /> Save to Documents
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Save to Documents</DialogTitle>
          <DialogDescription>
            Archive the First-Time Abatement PDF to this client's Letters folder.
          </DialogDescription>
        </DialogHeader>
        <Formik<Values>
          initialValues={{ docName: "First Time Abatements", overwrite: false }}
          validationSchema={schema}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting, values, setFieldValue }) => (
            <Form className="grid gap-4">
              <FormField name="docName" label="Document name" />
              <div className="flex items-center gap-2">
                <Checkbox
                  id="fta-overwrite"
                  checked={values.overwrite}
                  onCheckedChange={(v) => setFieldValue("overwrite", v === true)}
                />
                <Label htmlFor="fta-overwrite" className="text-sm font-normal">
                  Replace an existing document with the same name
                </Label>
              </div>
              <DialogFooter>
                <Button type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Saving…" : "Save"}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}
